import type { Driver } from "driver.js";
import { startSwivuGuide } from "./driver";
import { getGuideStatus, setGuideStatus } from "./guideStorage";
import { guideHasVisibleSteps } from "./selectors";
import { resolvePageGuide } from "./registry";
import type { GuideUserPreferences, ViewType } from "./types";

let activeGuideId: string | null = null;
let activeDriver: Driver | null = null;
let overlayBlockCount = 0;
let suppressGuideStatusPersistence = false;

type PageGuideUnblockListener = () => void;
const unblockListeners = new Set<PageGuideUnblockListener>();

function notifyPageGuideUnblocked(): void {
  if (overlayBlockCount !== 0) return;
  for (const listener of unblockListeners) {
    listener();
  }
}

export function subscribePageGuideUnblocked(listener: PageGuideUnblockListener): () => void {
  unblockListeners.add(listener);
  return () => unblockListeners.delete(listener);
}

export function isPageGuideBlocked(): boolean {
  return overlayBlockCount > 0;
}

export function dismissActivePageGuide(): void {
  if (!activeDriver) {
    activeGuideId = null;
    return;
  }
  suppressGuideStatusPersistence = true;
  activeDriver.destroy();
}

export function blockPageGuide(): void {
  overlayBlockCount += 1;
  dismissActivePageGuide();
}

export function unblockPageGuide(): void {
  const wasBlocked = overlayBlockCount > 0;
  overlayBlockCount = Math.max(0, overlayBlockCount - 1);
  if (wasBlocked && overlayBlockCount === 0) {
    notifyPageGuideUnblocked();
  }
}

function waitForGuideTargets(
  viewType: ViewType,
  attempts = 12,
  intervalMs = 150,
): Promise<ReturnType<typeof resolvePageGuide>> {
  return new Promise((resolve) => {
    let remaining = attempts;

    const tryResolve = () => {
      const guide = resolvePageGuide(viewType);
      if (guide && guideHasVisibleSteps(guide)) {
        resolve(guide);
        return;
      }

      remaining -= 1;
      if (remaining <= 0) {
        resolve(null);
        return;
      }

      window.setTimeout(tryResolve, intervalMs);
    };

    tryResolve();
  });
}

interface StartPageGuideOptions {
  viewType: ViewType;
  userPreferences: GuideUserPreferences | null | undefined;
  force?: boolean;
  zIndex?: number;
}

export async function startPageGuide({
  viewType,
  userPreferences,
  force = false,
  zIndex,
}: StartPageGuideOptions): Promise<boolean> {
  if (!force && isPageGuideBlocked()) return false;

  const guide = await waitForGuideTargets(viewType);
  if (!guide || (!force && isPageGuideBlocked())) return false;

  if (!force && getGuideStatus(userPreferences, guide.id)) {
    return false;
  }

  if (force) {
    if (activeDriver) dismissActivePageGuide();
    activeGuideId = null;
  } else if (activeGuideId === guide.id && activeDriver) {
    return false;
  }

  activeGuideId = guide.id;

  const driverObj = startSwivuGuide({
    steps: guide.steps,
    zIndex,
    onComplete: () => {
      activeGuideId = null;
      activeDriver = null;
      setGuideStatus(userPreferences, guide.id, "completed");
    },
    onDismiss: () => {
      activeGuideId = null;
      activeDriver = null;
      if (!suppressGuideStatusPersistence) {
        setGuideStatus(userPreferences, guide.id, "dismissed");
      }
      suppressGuideStatusPersistence = false;
    },
  });

  if (!driverObj) {
    activeGuideId = null;
    activeDriver = null;
    return false;
  }

  activeDriver = driverObj;
  return true;
}

export function replayPageGuide(
  viewType: ViewType,
  userPreferences: GuideUserPreferences | null | undefined,
  zIndex?: number,
): Promise<boolean> {
  return startPageGuide({ viewType, userPreferences, force: true, zIndex });
}
